/*
 * BrandPreviewPage.jsx — Soulumo mood board (Feb 24 → 25 2026)
 * --------------------------------------------------------
 * Public route at /brand/solumo. Renders three visual identity
 * variants side by side so Michael can decide with his eyes:
 *   1. CLASSIC     — current gold/ink palette, Cormorant wordmark
 *   2. LUMINOUS    — extended gold+rose-gold, italic wordmark
 *   3. MONOCHROME  — high-contrast, wide-track Hermès-style mark
 *
 * Each variant shows the wordmark, palette swatches, type
 * specimen, and a miniature hero-card preview so the emotional
 * feel of the brand is visible in-context — not just a logo.
 *
 * This page is intentionally rendered without the app shell (no
 * top nav, no ProtectedRoute) so Michael can shoot the URL to
 * anyone he wants a second opinion from.
 */
import React from "react";
import { Link } from "react-router-dom";

const TAGLINE = "Installations from within.";

/* ── Variant tokens ────────────────────────────────────────────
 * The three variants share the SAME navy base (#0d1417 — the
 * deep midnight ink already in the app, which Michael loves).
 * Only the gold treatment, wordmark, and secondary accents shift
 * between them. This keeps rebrand risk low: whichever variant
 * wins, the app's core palette barely moves. */
const NAVY = "#0d1417";
const NAVY_DEEP = "#0a1013";
const NAVY_LIFT = "#111a20";

const VARIANTS = [
  {
    key: "classic",
    label: "Classic",
    subtitle: "The house palette — sharpened",
    bg: NAVY,
    ink: NAVY,
    surface: "rgba(215,187,122,0.05)",
    surfaceBorder: "rgba(215,187,122,0.36)",
    gold: "#d7bb7a",
    goldSoft: "#c8a45a",
    cream: "#f5ecd9",
    creamDim: "rgba(245,236,217,0.72)",
    wordmarkFont: '"Cormorant Garamond", serif',
    wordmarkWeight: 400,
    wordmarkTracking: "0.03em",
    wordmarkItalic: false,
    swatches: [
      { label: "Navy Ink", hex: NAVY },
      { label: "Gold", hex: "#d7bb7a" },
      { label: "Gold Soft", hex: "#c8a45a" },
      { label: "Cream", hex: "#f5ecd9" },
    ],
    voice:
      "Zero disruption. The palette that already ships — the navy and gold Michael and Michelle keep coming back to — with the new name laid on top. Warmest, most familiar, fastest to launch.",
  },
  {
    key: "luminous",
    label: "Luminous",
    subtitle: "Navy at dusk — first light on the gold",
    bg: NAVY_LIFT,
    ink: NAVY_LIFT,
    surface: "rgba(224,169,122,0.06)",
    surfaceBorder: "rgba(224,169,122,0.32)",
    gold: "#e0a97a",
    goldSoft: "#f2c58d",
    cream: "#faeadc",
    creamDim: "rgba(250,234,220,0.72)",
    wordmarkFont: '"Cormorant Garamond", serif',
    wordmarkWeight: 300,
    wordmarkTracking: "0.06em",
    wordmarkItalic: true,
    swatches: [
      { label: "Navy Ink", hex: NAVY_LIFT },
      { label: "Rose Gold", hex: "#e0a97a" },
      { label: "Sunrise", hex: "#f2c58d" },
      { label: "Silk", hex: "#faeadc" },
    ],
    voice:
      "Same navy foundation, but the gold is pushed toward a first-light rose. Feminine-inclusive without going soft. The italic wordmark whispers. Same house, dawn window open.",
  },
  {
    key: "monochrome",
    label: "Monochrome",
    subtitle: "Navy + vellum — quiet luxury",
    bg: NAVY_DEEP,
    ink: NAVY_DEEP,
    surface: "rgba(245,236,217,0.03)",
    surfaceBorder: "rgba(245,236,217,0.18)",
    gold: "#e6d7ab",
    goldSoft: "#c8b485",
    cream: "#f8f4e8",
    creamDim: "rgba(248,244,232,0.7)",
    wordmarkFont: '"Cormorant Garamond", serif',
    wordmarkWeight: 500,
    wordmarkTracking: "0.22em",
    wordmarkItalic: false,
    swatches: [
      { label: "Navy Ink", hex: NAVY_DEEP },
      { label: "Vellum", hex: "#e6d7ab" },
      { label: "Bone", hex: "#c8b485" },
      { label: "Snow", hex: "#f8f4e8" },
    ],
    voice:
      "Navy stays, but the gold cools into a paler vellum. Wide-tracked uppercase. Fashion-house discipline. Feels like Hermès putting the deep navy foundation on the front cover of a coffee-table book.",
  },
  /* ── Feb 28 2026 · Michael + James ask for "more energy / magic"
   * — three duotone variants that split the wordmark colours so
   * "Soul" and "umo" carry different tones. */
  {
    key: "duotone-rose",
    label: "Duotone · Rose",
    subtitle: "Gold Soul, rose-sunrise umo",
    bg: NAVY,
    ink: NAVY,
    surface: "rgba(215,187,122,0.06)",
    surfaceBorder: "rgba(215,187,122,0.32)",
    gold: "#d7bb7a",
    goldSoft: "#e0a97a",
    cream: "#f5ecd9",
    creamDim: "rgba(245,236,217,0.72)",
    wordmarkFont: '"Cormorant Garamond", serif',
    wordmarkWeight: 400,
    wordmarkTracking: "0.02em",
    wordmarkItalic: false,
    soulColor:  "#f6d68a",   // warm gold, glowing
    umoColor:   "#e0a97a",   // rose sunrise
    umoOpacity: 0.9,
    umoWeight:  400,
    glowColor:  "#d7bb7a",
    swatches: [
      { label: "Navy Ink", hex: NAVY },
      { label: "Gold", hex: "#f6d68a" },
      { label: "Rose", hex: "#e0a97a" },
      { label: "Cream", hex: "#f5ecd9" },
    ],
    voice:
      "Warm gold Soul with a rose-sunrise umo — the moment the light hits the horizon. Most romantic of the set, works beautifully for the family / love verticals.",
  },
  {
    key: "duotone-aurum",
    label: "Duotone · Aurum",
    subtitle: "Bright gold Soul, moonlit-cream umo",
    bg: NAVY,
    ink: NAVY,
    surface: "rgba(215,187,122,0.06)",
    surfaceBorder: "rgba(215,187,122,0.32)",
    gold: "#f6d68a",
    goldSoft: "#c8a45a",
    cream: "#f5ecd9",
    creamDim: "rgba(245,236,217,0.72)",
    wordmarkFont: '"Cormorant Garamond", serif',
    wordmarkWeight: 400,
    wordmarkTracking: "0.02em",
    wordmarkItalic: false,
    soulColor:  "#ffd77a",   // 24-carat brilliance
    umoColor:   "#eef1ee",   // moonlit near-white
    umoOpacity: 0.75,
    umoWeight:  300,
    glowColor:  "#ffb84a",
    swatches: [
      { label: "Navy Ink", hex: NAVY },
      { label: "Aurum", hex: "#ffd77a" },
      { label: "Moonlit", hex: "#eef1ee" },
      { label: "Ember", hex: "#ffb84a" },
    ],
    voice:
      "Highest contrast, most obviously PREMIUM. Aurum gold shouts against the navy; moonlit umo whispers. Works best on marketing / press.",
  },
  {
    key: "duotone-ember",
    label: "Duotone · Ember",
    subtitle: "Cream Soul, ember-orange umo",
    bg: NAVY,
    ink: NAVY,
    surface: "rgba(224,122,80,0.06)",
    surfaceBorder: "rgba(224,122,80,0.32)",
    gold: "#e07a50",
    goldSoft: "#c85830",
    cream: "#faeadc",
    creamDim: "rgba(250,234,220,0.72)",
    wordmarkFont: '"Cormorant Garamond", serif',
    wordmarkWeight: 400,
    wordmarkTracking: "0.02em",
    wordmarkItalic: false,
    soulColor:  "#f8f0d8",   // soft cream
    umoColor:   "#e07a50",   // ember orange
    umoOpacity: 0.95,
    umoWeight:  500,
    glowColor:  "#e07a50",
    swatches: [
      { label: "Navy Ink", hex: NAVY },
      { label: "Cream", hex: "#f8f0d8" },
      { label: "Ember", hex: "#e07a50" },
      { label: "Deep Ember", hex: "#c85830" },
    ],
    voice:
      "Fire underneath. Cream Soul reads calm; ember umo is the heat rising. Boldest — the one that says the app CHANGES you.",
  },
];

/* ── Symbol · Soulumo Orb lockup ────────────────────────────────
 * Matches Michael & James's reference sketch (Feb 26 2026):
 *   ( ( ( ●  ) ) )   —  three open-parenthesis arcs of sound
 *   radiating outward from a luminous golden pearl.
 *
 * The reference shows the arcs as PARENTHESIS-style curves that
 * curve AWAY from the orb (like sound waves) — NOT concentric
 * circles. Left arcs open leftward, right arcs open rightward.
 * The pearl has a strong cream highlight upper-left and a warm
 * brown shadow lower-right giving it real 3D roundness. */
function SoulumoOrb({ color, size = 160 }) {
  const s = size;
  const cx = s / 2, cy = s / 2;
  const r = s * 0.155;                         // pearl radius
  // Arc positioning — three sets, each further out than the last.
  // Values tuned by eye to match the reference sketch density.
  const arcs = [
    { dx: r * 1.9,  h: r * 1.55, sw: 2.6, op: 0.98 },
    { dx: r * 2.75, h: r * 1.90, sw: 2.2, op: 0.75 },
    { dx: r * 3.65, h: r * 2.25, sw: 1.7, op: 0.42 },
  ];
  return (
    <svg
      width={s}
      height={s}
      viewBox={`0 0 ${s} ${s}`}
      fill="none"
      aria-hidden
      style={{ overflow: "visible" }}
    >
      <defs>
        {/* Pearl core — creamy centre → warm gold → brown edge */}
        <radialGradient id={`orb-core-${color}`} cx="0.36" cy="0.32" r="0.78">
          <stop offset="0"    stopColor="#fffbe8" stopOpacity="1" />
          <stop offset="0.20" stopColor="#ffe8b1" stopOpacity="1" />
          <stop offset="0.55" stopColor={color}   stopOpacity="1" />
          <stop offset="0.90" stopColor="#6b3f16" stopOpacity="1" />
          <stop offset="1"    stopColor="#3a2109" stopOpacity="1" />
        </radialGradient>
        {/* Outer halo — soft radiant glow around the orb */}
        <radialGradient id={`orb-halo-${color}`} cx="0.5" cy="0.5" r="0.5">
          <stop offset="0"    stopColor="#fff2c2" stopOpacity="0.55" />
          <stop offset="0.45" stopColor={color}   stopOpacity="0.32" />
          <stop offset="0.85" stopColor={color}   stopOpacity="0.05" />
          <stop offset="1"    stopColor={color}   stopOpacity="0" />
        </radialGradient>
        {/* Arc gradient — bright gold in the middle of each arc,
            fading toward the ends so they read as luminous curves */}
        <linearGradient id={`arc-${color}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0"    stopColor={color} stopOpacity="0.12" />
          <stop offset="0.5"  stopColor="#ffe8b1" stopOpacity="1" />
          <stop offset="1"    stopColor={color} stopOpacity="0.12" />
        </linearGradient>
      </defs>

      {/* Outer halo behind everything */}
      <circle cx={cx} cy={cy} r={r * 2.6} fill={`url(#orb-halo-${color})`} />

      {/* LEFT parenthesis-style arcs · opens to the LEFT
          Cubic Bézier for a real parenthesis curve.
          Start (top-right of arc) — bows leftward — End (bottom-right of arc). */}
      {arcs.map((a, i) => {
        const x2 = cx - a.dx;                  // right edge of the arc (closest to orb)
        const yTop = cy - a.h;
        const yBot = cy + a.h;
        const ctrlX = cx - a.dx - a.h * 0.85;  // bow left by ~85% of half-height
        return (
          <path
            key={`la-${i}`}
            d={`M ${x2} ${yTop} C ${ctrlX} ${cy - a.h * 0.55}, ${ctrlX} ${cy + a.h * 0.55}, ${x2} ${yBot}`}
            stroke={`url(#arc-${color})`}
            strokeWidth={a.sw}
            strokeLinecap="round"
            fill="none"
            opacity={a.op}
          />
        );
      })}

      {/* RIGHT parenthesis-style arcs · opens to the RIGHT */}
      {arcs.map((a, i) => {
        const x2 = cx + a.dx;
        const yTop = cy - a.h;
        const yBot = cy + a.h;
        const ctrlX = cx + a.dx + a.h * 0.85;
        return (
          <path
            key={`ra-${i}`}
            d={`M ${x2} ${yTop} C ${ctrlX} ${cy - a.h * 0.55}, ${ctrlX} ${cy + a.h * 0.55}, ${x2} ${yBot}`}
            stroke={`url(#arc-${color})`}
            strokeWidth={a.sw}
            strokeLinecap="round"
            fill="none"
            opacity={a.op}
          />
        );
      })}

      {/* The pearl itself */}
      <circle cx={cx} cy={cy} r={r} fill={`url(#orb-core-${color})`} />

      {/* Bright specular highlight — the "wet glass" gleam upper-left */}
      <ellipse
        cx={cx - r * 0.36}
        cy={cy - r * 0.44}
        rx={r * 0.42}
        ry={r * 0.24}
        fill="#ffffff"
        opacity="0.75"
      />
      {/* Softer secondary highlight just below the primary */}
      <ellipse
        cx={cx - r * 0.10}
        cy={cy - r * 0.15}
        rx={r * 0.18}
        ry={r * 0.10}
        fill="#fff8dc"
        opacity="0.55"
      />
    </svg>
  );
}

/* ── Wordmark · Reclaim-style with subtle Soul/umo split
 * Mar 3 2026 · Michael asked to see the same clean Manrope
 * uppercase treatment but with "SOUL" in a different shade
 * from "UMO" — quiet duotone, no glow. Middle "Luminous" pick
 * (rose-gold SOUL + silk UMO) rolled out app-wide. */
function SoulumoWordmark({ v }) {
  // Preserve the per-variant duotone story on the brand-preview
  // page — each variant chose its own soul/umo pair. If a variant
  // didn't specify, fall back to the app-wide Luminous default.
  const soulColor = v.soulColor || v.gold || "#e0a97a";
  const umoColor  = v.umoColor  || v.cream || "#f5e0c5";
  return (
    <div
      className="flex items-baseline justify-center mt-2"
      style={{
        fontFamily: '"Manrope", system-ui, sans-serif',
        letterSpacing: "0.32em",
        fontSize: "clamp(1.9rem, 5vw, 3.2rem)",
        fontWeight: 300,
        lineHeight: 1,
        textTransform: "uppercase",
        maxWidth: "100%",
        whiteSpace: "nowrap",
      }}
      data-testid={`brand-wordmark-${v.key}`}
    >
      <span style={{ color: soulColor }}>Soul</span>
      <span style={{ color: umoColor, marginLeft: "0.04em" }}>umo</span>
    </div>
  );
}

/* ── Mini hero card preview (renders inside each variant) ──── */
function VariantHero({ v }) {
  return (
    <div
      className="rounded-2xl p-6 mt-6"
      style={{
        background: v.surface,
        border: `1px solid ${v.surfaceBorder}`,
        color: v.cream,
      }}
      data-testid={`brand-hero-${v.key}`}
    >
      <div
        className="text-[0.6rem] uppercase mb-4"
        style={{ color: v.goldSoft, letterSpacing: "0.32em", fontFamily: '"Manrope", sans-serif' }}
      >
        Tonight&apos;s Installation
      </div>
      <h3
        style={{
          fontFamily: '"Cormorant Garamond", serif',
          fontSize: "1.9rem",
          fontWeight: 300,
          lineHeight: 1.15,
          color: v.cream,
        }}
      >
        A quieter version of the man you woke up as.
      </h3>
      <p
        className="italic mt-3"
        style={{
          fontFamily: '"Cormorant Garamond", serif',
          color: v.creamDim,
          fontSize: "0.95rem",
        }}
      >
        Twelve minutes. Your voice. Music beneath.
      </p>
      <div className="mt-6 flex items-center gap-3">
        <button
          style={{
            background: v.gold,
            color: v.ink,
            border: "none",
            padding: "0.75rem 1.25rem",
            borderRadius: "9999px",
            fontFamily: '"Manrope", sans-serif',
            letterSpacing: "0.22em",
            textTransform: "uppercase",
            fontSize: "0.66rem",
            fontWeight: 600,
          }}
          disabled
        >
          Begin
        </button>
        <span
          style={{
            fontFamily: '"Manrope", sans-serif',
            fontSize: "0.62rem",
            letterSpacing: "0.28em",
            textTransform: "uppercase",
            color: v.goldSoft,
          }}
        >
          Play now ✦
        </span>
      </div>
    </div>
  );
}

/* ── Variant card ──────────────────────────────────────────────── */
function VariantCard({ v }) {
  return (
    <article
      className="rounded-3xl overflow-hidden"
      style={{ background: v.bg, border: "1px solid rgba(255,255,255,0.06)" }}
      data-testid={`brand-variant-${v.key}`}
    >
      {/* Header · wordmark */}
      <div
        className="px-8 pt-10 pb-8 flex flex-col items-center text-center"
        style={{
          borderBottom: `1px solid ${v.surfaceBorder}`,
          background: `radial-gradient(ellipse at top, ${v.gold}12 0%, transparent 70%)`,
        }}
      >
        <div
          className="text-[0.58rem] uppercase mb-6"
          style={{ color: v.goldSoft, letterSpacing: "0.36em", fontFamily: '"Manrope", sans-serif' }}
        >
          {v.label} · {v.subtitle}
        </div>
        <div className="flex items-center justify-center mb-6 relative">
          {/* Pulsing magic halo — subtle breathing aura behind the
              orb. Michael + James asked for "more energy / magic".
              CSS keyframes so it works even in the printed / static
              screenshot renderer. */}
          <span
            aria-hidden
            className="absolute rounded-full"
            style={{
              width: 260, height: 260,
              background: `radial-gradient(circle, ${v.glowColor || v.gold}55 0%, ${v.glowColor || v.gold}00 62%)`,
              filter: "blur(24px)",
              animation: "soulumoBreath 5.6s ease-in-out infinite",
            }}
          />
          <SoulumoOrb color={v.gold} size={148} />
        </div>
        <SoulumoWordmark v={v} />
        <div
          className="mt-4 italic"
          style={{
            fontFamily: '"Cormorant Garamond", serif',
            fontSize: "1rem",
            color: v.creamDim,
          }}
        >
          {TAGLINE}
        </div>
      </div>

      {/* Palette */}
      <div className="px-8 py-6">
        <div
          className="text-[0.55rem] uppercase mb-3"
          style={{ color: v.goldSoft, letterSpacing: "0.28em", fontFamily: '"Manrope", sans-serif' }}
        >
          Palette
        </div>
        <div className="grid grid-cols-4 gap-2">
          {v.swatches.map((s) => (
            <div key={s.hex} className="text-center">
              <div
                className="w-full aspect-square rounded-lg"
                style={{ background: s.hex, border: `1px solid ${v.surfaceBorder}` }}
              />
              <div
                className="mt-1 text-[0.55rem] uppercase"
                style={{ color: v.creamDim, letterSpacing: "0.16em", fontFamily: '"Manrope", sans-serif' }}
              >
                {s.label}
              </div>
              <div
                className="text-[0.55rem]"
                style={{ color: v.creamDim, fontFamily: '"Manrope", sans-serif', opacity: 0.6 }}
              >
                {s.hex.toUpperCase()}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Type specimen */}
      <div className="px-8 pb-6">
        <div
          className="text-[0.55rem] uppercase mb-3"
          style={{ color: v.goldSoft, letterSpacing: "0.28em", fontFamily: '"Manrope", sans-serif' }}
        >
          Type specimen
        </div>
        <div style={{ borderTop: `1px solid ${v.surfaceBorder}`, paddingTop: "0.9rem" }}>
          <div
            style={{
              fontFamily: '"Cormorant Garamond", serif',
              fontWeight: 300,
              fontSize: "2rem",
              lineHeight: 1.1,
              color: v.cream,
            }}
          >
            Installations from within.
          </div>
          <div
            className="mt-3"
            style={{
              fontFamily: '"Manrope", sans-serif',
              fontSize: "0.86rem",
              color: v.creamDim,
              lineHeight: 1.55,
            }}
          >
            Your own voice, spoken back to you — over music, over
            breath, over the frequency your nervous system was
            waiting for. Twelve minutes at a time. Yours.
          </div>
          <div
            className="mt-3 text-[0.6rem] uppercase"
            style={{ color: v.gold, letterSpacing: "0.28em", fontFamily: '"Manrope", sans-serif' }}
          >
            Begin tonight · Play now ✦
          </div>
        </div>
      </div>

      {/* Hero preview */}
      <div className="px-6 pb-8">
        <VariantHero v={v} />
      </div>

      {/* Voice / rationale */}
      <div
        className="px-8 py-5"
        style={{ borderTop: `1px solid ${v.surfaceBorder}`, background: v.surface }}
      >
        <div
          className="text-[0.55rem] uppercase mb-2"
          style={{ color: v.goldSoft, letterSpacing: "0.28em", fontFamily: '"Manrope", sans-serif' }}
        >
          Feel
        </div>
        <p
          className="italic"
          style={{
            fontFamily: '"Cormorant Garamond", serif',
            fontSize: "0.95rem",
            color: v.creamDim,
            lineHeight: 1.5,
          }}
        >
          {v.voice}
        </p>
      </div>
    </article>
  );
}

/* ── Page ─────────────────────────────────────────────────────── */
export default function BrandPreviewPage() {
  return (
    <div
      style={{
        minHeight: "100vh",
        background: "#0a1013",
        color: "#f5ecd9",
      }}
      data-testid="brand-preview-page"
    >
      <style>{`
        @keyframes soulumoBreath {
          0%, 100% { transform: scale(1);   opacity: 0.55; }
          50%      { transform: scale(1.14); opacity: 0.95; }
        }
      `}</style>
      {/* Intro */}
      <header className="max-w-6xl mx-auto px-6 pt-12 pb-8">
        <Link
          to="/"
          className="text-[0.6rem] uppercase"
          style={{
            color: "#c8a45a",
            letterSpacing: "0.32em",
            fontFamily: '"Manrope", sans-serif',
            textDecoration: "none",
          }}
          data-testid="brand-preview-home-link"
        >
          ← Back to app
        </Link>
        <h1
          className="mt-6"
          style={{
            fontFamily: '"Cormorant Garamond", serif',
            fontSize: "clamp(2.4rem, 5.5vw, 4rem)",
            fontWeight: 300,
            lineHeight: 1.02,
            color: "#f5ecd9",
          }}
        >
          Soulumo.
          <span
            className="italic block mt-2"
            style={{ color: "rgba(245,236,217,0.55)", fontSize: "clamp(1.1rem, 2.2vw, 1.6rem)" }}
          >
            Three ways it could feel.
          </span>
        </h1>
        <p
          className="mt-6 max-w-2xl italic"
          style={{
            fontFamily: '"Cormorant Garamond", serif',
            color: "rgba(245,236,217,0.7)",
            fontSize: "1.05rem",
            lineHeight: 1.55,
          }}
        >
          A name is a doorway. Below are three doorways for
          <b> Soulumo </b>— the same brand, in three different
          registers. Feel each one. Live inside it for a moment.
          The one you don&apos;t want to leave is the answer.
        </p>
        <div
          className="mt-4 text-[0.62rem] uppercase"
          style={{ color: "#c8a45a", letterSpacing: "0.28em", fontFamily: '"Manrope", sans-serif' }}
        >
          Clearance · No wellness-app conflicts found · .app / .ai likely open
        </div>
        <div
          className="mt-2 text-[0.62rem] uppercase"
          style={{ color: "rgba(215,187,122,0.55)", letterSpacing: "0.28em", fontFamily: '"Manrope", sans-serif' }}
        >
          Navy foundation stays across all three · gold treatment is what varies
        </div>
      </header>

      {/* Grid of variants */}
      <section
        className="max-w-7xl mx-auto px-6 pb-16"
        style={{
          display: "grid",
          gap: "1.5rem",
          gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
        }}
      >
        {VARIANTS.map((v) => (
          <VariantCard key={v.key} v={v} />
        ))}
      </section>

      {/* Footer · comparison table */}
      <section
        className="max-w-5xl mx-auto px-6 pb-24"
        data-testid="brand-preview-compare"
      >
        <h2
          className="text-center mb-8"
          style={{
            fontFamily: '"Cormorant Garamond", serif',
            fontSize: "clamp(1.6rem, 3.5vw, 2.4rem)",
            fontWeight: 300,
            color: "#f5ecd9",
          }}
        >
          Which door do you not want to leave?
        </h2>
        <div
          className="rounded-2xl overflow-hidden"
          style={{ border: "1px solid rgba(215,187,122,0.24)" }}
        >
          <table
            className="w-full text-left"
            style={{ borderCollapse: "collapse", fontFamily: '"Manrope", sans-serif' }}
          >
            <thead>
              <tr
                style={{
                  background: "rgba(215,187,122,0.06)",
                  color: "#c8a45a",
                  fontSize: "0.6rem",
                  letterSpacing: "0.28em",
                  textTransform: "uppercase",
                }}
              >
                <th className="px-4 py-3">Variant</th>
                <th className="px-4 py-3">Feels like</th>
                <th className="px-4 py-3">Best for</th>
                <th className="px-4 py-3">Risk</th>
              </tr>
            </thead>
            <tbody style={{ fontSize: "0.82rem", color: "rgba(245,236,217,0.85)" }}>
              <tr style={{ borderTop: "1px solid rgba(215,187,122,0.16)" }}>
                <td className="px-4 py-4" style={{ color: "#f5ecd9" }}>Classic</td>
                <td className="px-4 py-4">Candlelit study, private atelier</td>
                <td className="px-4 py-4">Zero rebrand work; the navy + gold you already love, sharpened</td>
                <td className="px-4 py-4">Feels close to today — less &quot;new house&quot;</td>
              </tr>
              <tr style={{ borderTop: "1px solid rgba(215,187,122,0.16)" }}>
                <td className="px-4 py-4" style={{ color: "#f5ecd9" }}>Luminous</td>
                <td className="px-4 py-4">Navy at sunrise, first light</td>
                <td className="px-4 py-4">Broadest audience; navy stays, gold warms into rose</td>
                <td className="px-4 py-4">Rose-gold takes a full accent re-pass across the app</td>
              </tr>
              <tr style={{ borderTop: "1px solid rgba(215,187,122,0.16)" }}>
                <td className="px-4 py-4" style={{ color: "#f5ecd9" }}>Monochrome</td>
                <td className="px-4 py-4">Fashion house on navy — museum-quiet</td>
                <td className="px-4 py-4">Highest brand ceiling; navy + paler vellum-gold</td>
                <td className="px-4 py-4">Hardest to translate to daily app UI without cooling off</td>
              </tr>
            </tbody>
          </table>
        </div>

        <p
          className="text-center mt-8 italic"
          style={{ color: "rgba(245,236,217,0.62)", fontFamily: '"Cormorant Garamond", serif' }}
        >
          Send this URL to anyone you trust. The right name is the one
          the group can&apos;t stop saying out loud.
        </p>
      </section>
    </div>
  );
}
